import { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  getDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../firebase";
import { awardPoints } from "./Expenses";

export default function LocalTips({ onBack }) {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchCity, setSearchCity] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [profile, setProfile] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const [form, setForm] = useState({
    city: "",
    country: "",
    category: "Food",
    title: "",
    tip: "",
    bestTime: "",
    visitedDate: "",
  });

  const categories = ["All", "Food", "Culture", "Nature", "Nightlife", "Shopping", "Transport", "Hidden Gems"];
  const categoryColors = {
    Food: { bg: "FAEEDA", text: "633806" },
    Culture: { bg: "e3eef6", text: "1a3a5c" },
    Nature: { bg: "E1F5EE", text: "085041" },
    Nightlife: { bg: "EDE0F5", text: "4A1080" },
    Shopping: { bg: "FAECE7", text: "712B13" },
    Transport: { bg: "E8F4FD", text: "0D4F8B" },
    "Hidden Gems": { bg: "FFF8E1", text: "5D4037" },
  };

  useEffect(() => {
    fetchTips();
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    if (!auth.currentUser) return;
    try {
      const snap = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (snap.exists()) setProfile(snap.data());
    } catch (err) {
      console.error("LocalTips.fetchProfile failed:", err);
    }
  };

  const fetchTips = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const q = query(collection(db, "tips"), orderBy("createdAt", "desc"), limit(50));
      const snap = await getDocs(q);
      setTips(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      setFetchError(true);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.city || !form.title || !form.tip) return;
    if (!auth.currentUser) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, "tips"), {
        ...form,
        authorId: auth.currentUser.uid,
        authorName: profile?.name || "Anonymous",
        authorPhoto: profile?.photoURL || null,
        likes: [],
        createdAt: new Date(),
      });
      await awardPoints(auth.currentUser.uid, 50, "Shared a local tip");
      setForm({ city: "", country: "", category: "Food", title: "", tip: "", bestTime: "", visitedDate: "" });
      setShowForm(false);
      fetchTips();
    } catch (err) {
      console.error(err);
      setSubmitError("Failed to share tip. Please try again.");
    }
    setSubmitting(false);
  };

  const handleLike = async (tipId, currentLikes) => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const tipRef = doc(db, "tips", tipId);
    const isArray = Array.isArray(currentLikes);
    const previousTips = tips;
    try {
      if (isArray && currentLikes.includes(uid)) {
        await updateDoc(tipRef, { likes: arrayRemove(uid) });
        setTips(tips.map(t => t.id === tipId
          ? { ...t, likes: t.likes.filter(l => l !== uid) }
          : t));
      } else if (isArray) {
        await updateDoc(tipRef, { likes: arrayUnion(uid) });
        setTips(tips.map(t => t.id === tipId
          ? { ...t, likes: [...t.likes, uid] }
          : t));
      } else {
        await updateDoc(tipRef, { likes: [uid] });
        setTips(tips.map(t => t.id === tipId ? { ...t, likes: [uid] } : t));
      }
    } catch (err) {
      console.error(err);
      setTips(previousTips);
      setSubmitError("Failed to update. Please try again.");
    }
  };

  const filteredTips = tips.filter(t => {
    const matchesCategory = activeCategory === "All" || t.category === activeCategory;
    const matchesCity = !filterCity || t.city?.toLowerCase().includes(filterCity.toLowerCase());
    return matchesCategory && matchesCity;
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .lt-root { min-height: 100vh; background: #FAF6F0; font-family: 'DM Sans', sans-serif; padding-bottom: 40px; }

        .lt-nav { background: white; border-bottom: 0.5px solid rgba(26,23,20,0.08); padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 100; }
        .lt-back { font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.55); cursor: pointer; background: none; border: none; font-family: 'DM Sans', sans-serif; }
        .lt-back:hover { color: #F5A623; }
        .lt-nav-title { font-family: 'Playfair Display', serif; font-size: 16px; font-weight: 600; color: #1a1714; }
        .lt-add-btn { font-size: 12px; font-weight: 500; color: white; background: #F5A623; border: none; border-radius: 100px; padding: 7px 16px; cursor: pointer; font-family: 'DM Sans', sans-serif; }

        .lt-hero { background: #1E2D4A; padding: 24px 24px 32px; }
        .lt-hero-tag { display: inline-block; background: rgba(123,167,196,0.2); border: 1px solid rgba(123,167,196,0.3); color: #7BA7C4; font-size: 11px; font-weight: 500; padding: 4px 10px; border-radius: 100px; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
        .lt-hero-title { font-family: 'Playfair Display', serif; font-size: 24px; font-weight: 600; color: white; margin-bottom: 14px; line-height: 1.2; }
        .lt-hero-title em { font-style: italic; color: #7BA7C4; }

        .lt-search { display: flex; gap: 8px; }
        .lt-search-input { flex: 1; padding: 10px 14px; border: none; border-radius: 10px; background: rgba(255,255,255,0.1); color: white; font-size: 14px; font-family: 'DM Sans', sans-serif; outline: none; }
        .lt-search-input::placeholder { color: rgba(255,255,255,0.4); }
        .lt-search-btn { padding: 10px 16px; background: #F5A623; color: white; border: none; border-radius: 10px; font-size: 13px; font-weight: 500; cursor: pointer; font-family: 'DM Sans', sans-serif; white-space: nowrap; }

        .lt-cats { display: flex; gap: 8px; overflow-x: auto; padding: 16px 24px; background: white; border-bottom: 0.5px solid rgba(26,23,20,0.06); scrollbar-width: none; }
        .lt-cats::-webkit-scrollbar { display: none; }
        .lt-cat { padding: 6px 14px; border-radius: 100px; border: 1.5px solid rgba(26,23,20,0.12); background: white; font-size: 12px; font-weight: 500; color: rgba(26,23,20,0.6); cursor: pointer; font-family: 'DM Sans', sans-serif; white-space: nowrap; transition: all 0.15s; }
        .lt-cat.active { background: #F5A623; color: white; border-color: #F5A623; }

        .lt-content { padding: 20px; }

        .lt-empty { background: white; border-radius: 16px; padding: 40px 24px; text-align: center; border: 1.5px dashed rgba(26,23,20,0.12); }
        .lt-empty-icon { font-size: 36px; margin-bottom: 12px; }
        .lt-empty-title { font-size: 16px; font-weight: 500; color: #1a1714; margin-bottom: 6px; }
        .lt-empty-sub { font-size: 13px; color: rgba(26,23,20,0.45); margin-bottom: 20px; }
        .lt-empty-btn { padding: 10px 20px; background: #F5A623; color: white; border: none; border-radius: 100px; font-size: 13px; font-weight: 500; cursor: pointer; font-family: 'DM Sans', sans-serif; }

        .lt-tip-card { background: white; border-radius: 16px; border: 0.5px solid rgba(26,23,20,0.08); padding: 18px; margin-bottom: 12px; box-shadow: 0 2px 12px rgba(26,23,20,0.04); }
        .lt-tip-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
        .lt-cat-badge { font-size: 10px; font-weight: 500; padding: 3px 10px; border-radius: 100px; }
        .lt-tip-location { font-size: 12px; color: rgba(26,23,20,0.45); margin-top: 2px; }
        .lt-tip-title { font-size: 15px; font-weight: 500; color: #1a1714; margin-bottom: 6px; font-family: 'Playfair Display', serif; }
        .lt-tip-text { font-size: 13px; color: rgba(26,23,20,0.65); line-height: 1.6; font-weight: 300; margin-bottom: 12px; }
        .lt-tip-best { display: flex; align-items: center; gap: 6px; font-size: 11px; color: rgba(26,23,20,0.45); margin-bottom: 12px; }
        .lt-tip-footer { display: flex; align-items: center; justify-content: space-between; }
        .lt-tip-author { display: flex; align-items: center; gap: 8px; }
        .lt-author-av { width: 26px; height: 26px; border-radius: 50%; background: #1E2D4A; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 500; color: white; flex-shrink: 0; overflow: hidden; }
        .lt-author-av img { width: 100%; height: 100%; object-fit: cover; }
        .lt-author-name { font-size: 12px; color: rgba(26,23,20,0.55); }
        .lt-like-btn { display: flex; align-items: center; gap: 5px; background: none; border: none; cursor: pointer; font-family: 'DM Sans', sans-serif; font-size: 12px; color: rgba(26,23,20,0.45); padding: 4px 8px; border-radius: 100px; transition: all 0.15s; }
        .lt-like-btn.liked { color: #c9622f; background: #fef0ed; }
        .lt-like-btn:hover { background: #FAF6F0; }

        .lt-modal-overlay { position: fixed; inset: 0; background: rgba(26,23,20,0.5); z-index: 200; display: flex; align-items: flex-end; justify-content: center; }
        .lt-modal { background: white; border-radius: 20px 20px 0 0; padding: 28px 24px; width: 100%; max-width: 600px; max-height: 90vh; overflow-y: auto; animation: slideUp 0.3s ease; }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .lt-modal-title { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 600; color: #1a1714; margin-bottom: 20px; }
        .lt-field { margin-bottom: 14px; }
        .lt-label { display: block; font-size: 12px; font-weight: 500; color: rgba(26,23,20,0.55); margin-bottom: 5px; }
        .lt-input { width: 100%; padding: 12px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; }
        .lt-input:focus { border-color: #F5A623; background: white; }
        .lt-textarea { width: 100%; padding: 12px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 10px; background: #fafaf8; font-size: 14px; font-family: 'DM Sans', sans-serif; color: #1a1714; outline: none; resize: none; height: 90px; }
        .lt-textarea:focus { border-color: #F5A623; background: white; }
        .lt-cat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
        .lt-cat-opt { padding: 8px 4px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 8px; font-size: 11px; font-weight: 500; color: rgba(26,23,20,0.6); cursor: pointer; font-family: 'DM Sans', sans-serif; text-align: center; transition: all 0.15s; background: white; }
        .lt-cat-opt.selected { background: #F5A623; color: white; border-color: #F5A623; }
        .lt-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .lt-submit-btn { width: 100%; padding: 14px; background: #F5A623; color: white; border: none; border-radius: 12px; font-size: 15px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: pointer; margin-top: 8px; }
        .lt-cancel-btn { width: 100%; padding: 12px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 12px; background: white; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.5); cursor: pointer; font-family: 'DM Sans', sans-serif; margin-top: 8px; }
      `}</style>

      <div className="lt-root">
        <div className="lt-nav">
          <button className="lt-back" onClick={onBack}>← Back</button>
          <span className="lt-nav-title">Local Tips</span>
          <button className="lt-add-btn" onClick={() => setShowForm(true)}>+ Share a tip</button>
        </div>

        <div className="lt-hero">
          <div className="lt-hero-tag">Community</div>
          <h1 className="lt-hero-title">Tips from people<br /><em>who actually live there.</em></h1>
          <div className="lt-search">
            <input
              className="lt-search-input"
              placeholder="Search by city..."
              value={searchCity}
              onChange={e => setSearchCity(e.target.value)}
              onKeyDown={e => e.key === "Enter" && setFilterCity(searchCity)}
            />
            <button className="lt-search-btn" onClick={() => setFilterCity(searchCity)}>
              Search
            </button>
            {filterCity && (
              <button className="lt-search-btn" style={{background:'rgba(255,255,255,0.15)'}} onClick={() => { setFilterCity(""); setSearchCity(""); }}>
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="lt-cats">
          {categories.map(cat => (
            <button key={cat} className={`lt-cat ${activeCategory === cat ? "active" : ""}`} onClick={() => setActiveCategory(cat)}>
              {cat}
            </button>
          ))}
        </div>

        <div className="lt-content">
          {loading ? (
            <p style={{textAlign:'center', color:'rgba(26,23,20,0.4)', fontSize:14, paddingTop:20}}>Loading tips...</p>
          ) : fetchError ? (
            <div className="lt-empty">
              <div className="lt-empty-icon">⚠️</div>
              <div className="lt-empty-title">Couldn't load tips</div>
              <div className="lt-empty-sub">Please try again.</div>
              <button className="lt-empty-btn" onClick={fetchTips}>Retry</button>
            </div>
          ) : filteredTips.length === 0 ? (
            <div className="lt-empty">
              <div className="lt-empty-icon">📍</div>
              <div className="lt-empty-title">No tips yet{filterCity ? ` for ${filterCity}` : ""}</div>
              <div className="lt-empty-sub">Be the first to share a local tip and help fellow travelers discover something amazing.</div>
              <button className="lt-empty-btn" onClick={() => setShowForm(true)}>Share the first tip</button>
            </div>
          ) : (
            filteredTips.map(tip => {
              const colors = categoryColors[tip.category] || { bg: "e3eef6", text: "1a3a5c" };
              const liked = !!auth.currentUser?.uid && Array.isArray(tip.likes) && tip.likes.includes(auth.currentUser.uid);
              return (
                <div key={tip.id} className="lt-tip-card">
                  <div className="lt-tip-top">
                    <div>
                      <span className="lt-cat-badge" style={{background:`#${colors.bg}`, color:`#${colors.text}`}}>
                        {tip.category}
                      </span>
                      <div className="lt-tip-location">📍 {tip.city}{tip.country ? `, ${tip.country}` : ""}</div>
                    </div>
                  </div>
                  <div className="lt-tip-title">{tip.title}</div>
                  <div className="lt-tip-text">{tip.tip}</div>
                  {tip.bestTime && (
                    <div className="lt-tip-best">⏰ Best time: {tip.bestTime}</div>
                  )}
                  {tip.visitedDate && (
                    <div className="lt-tip-best">📅 Visited: {tip.visitedDate}</div>
                  )}
                  <div className="lt-tip-footer">
                    <div className="lt-tip-author">
                      <div className="lt-author-av">
                        {tip.authorPhoto ? <img src={tip.authorPhoto} alt="" /> : tip.authorName?.charAt(0).toUpperCase()}
                      </div>
                      <span className="lt-author-name">{tip.authorName}</span>
                    </div>
                    <button className={`lt-like-btn ${liked ? "liked" : ""}`} onClick={() => handleLike(tip.id, tip.likes)}>
                      {liked ? "♥" : "♡"} {Array.isArray(tip.likes) ? tip.likes.length : (typeof tip.likes === 'number' ? tip.likes : 0)}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showForm && (
        <div className="lt-modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="lt-modal">
            <div className="lt-modal-title">Share a local tip 📍</div>

            <div className="lt-field">
              <label className="lt-label">Category</label>
              <div className="lt-cat-grid">
                {["Food", "Culture", "Nature", "Nightlife", "Shopping", "Transport", "Hidden Gems"].map(cat => (
                  <button key={cat} className={`lt-cat-opt ${form.category === cat ? "selected" : ""}`}
                    onClick={() => setForm({...form, category: cat})}>
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="lt-row">
              <div className="lt-field">
                <label className="lt-label">City</label>
                <input className="lt-input" placeholder="e.g. Bali" value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
              </div>
              <div className="lt-field">
                <label className="lt-label">Country</label>
                <input className="lt-input" placeholder="e.g. Indonesia" value={form.country} onChange={e => setForm({...form, country: e.target.value})} />
              </div>
            </div>

            <div className="lt-field">
              <label className="lt-label">Tip title</label>
              <input className="lt-input" placeholder="e.g. Best warungs are away from the main street" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            </div>

            <div className="lt-field">
              <label className="lt-label">Your tip</label>
              <textarea className="lt-textarea" placeholder="Share what you know — the more specific the better!" value={form.tip} onChange={e => setForm({...form, tip: e.target.value})} />
            </div>

            <div className="lt-field">
              <label className="lt-label">Best time to go <span style={{color:'rgba(26,23,20,0.35)'}}>— optional</span></label>
              <input className="lt-input" placeholder="e.g. Early morning before 9am" value={form.bestTime} onChange={e => setForm({...form, bestTime: e.target.value})} />
            </div>

            <div className="lt-field">
              <label className="lt-label">When did you visit? <span style={{color:'rgba(26,23,20,0.35)'}}>— optional but helpful</span></label>
              <input className="lt-input" placeholder="e.g. March 2024" value={form.visitedDate} onChange={e => setForm({...form, visitedDate: e.target.value})} />
            </div>

            <button className="lt-submit-btn" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Sharing..." : "Share tip →"}
            </button>
            {submitError && (
              <p style={{fontSize:13,color:'#c9622f',marginTop:8}}>{submitError}</p>
            )}
            <button className="lt-cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );
}